function b64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Messaggio leggibile a partire dallo stato HTTP.
 *
 * Serve perche' i due guasti piu' probabili dell'editor arrivavano entrambi
 * come una stringa che non dice niente all'utente:
 *
 * - 404: il job non e' piu' in Redis. I documenti hanno un TTL di circa un'ora,
 *   dopo di che l'originale in cache non c'e' piu' e l'editor non puo'
 *   funzionare. Non e' un errore: e' una sessione scaduta, e la via d'uscita e'
 *   rilanciare l'anonimizzazione.
 * - 5xx: il servizio dietro non ha risposto. Prima l'utente leggeva
 *   `Errore dal servizio AWS: {"message":"Service Unavailable"}`.
 *
 * @param {Response} res
 * @param {any} data corpo gia' decodificato, se c'era
 */
function messaggioErrore(res, data) {
  if (res.status === 404) {
    return "Questo documento non è più disponibile sul server: i file restano "
      + "in memoria circa un'ora dopo l'anonimizzazione, poi vengono eliminati. "
      + "Per modificarlo ancora, rilancia l'anonimizzazione.";
  }
  if (res.status === 402 || res.status === 403) {
    return "Questo documento non risulta sbloccato. Completa il pagamento e riprova.";
  }
  if (res.status === 413) {
    return "Il documento è troppo grande per essere rielaborato dall'editor.";
  }
  if (res.status >= 500) {
    return "Il servizio di anonimizzazione non ha risposto. Riprova fra un minuto: "
      + "se l'errore si ripete, segnalalo indicando il nome del documento.";
  }
  const dal = data?.error || data?.detail;
  return typeof dal === "string" && dal ? dal : `Errore ${res.status}`;
}

/**
 * L'editor e /redact restituiscono il documento completo: il backend li
 * concede a chi ha un abbonamento (sessione nel cookie) oppure a chi mostra
 * la sessione Stripe pagata di quel lavoro. Per il flusso a consumo la
 * sessione viaggia qui, come `session_id`.
 *
 * @param {string} jobId
 * @param {number} fileIndex
 * @param {string|null} [sessionId] sessione Stripe pagata (flusso a consumo)
 */
export async function fetchEditorPayload(jobId, fileIndex, sessionId = null) {
  const qs = new URLSearchParams({ file_index: String(fileIndex) });
  if (sessionId) qs.set("session_id", sessionId);
  const url = `/api/jobs/${encodeURIComponent(jobId)}/editor?${qs.toString()}`;
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messaggioErrore(res, data));
  }
  if (!data?.document_b64) {
    throw new Error("Risposta editor senza documento PDF.");
  }
  return {
    jobId: data.job_id || jobId,
    fileIndex: data.file_index ?? fileIndex,
    originalFilename: data.original_filename || "",
    pdfFilename: data.pdf_filename || "document.pdf",
    pdfBytes: b64ToArrayBuffer(data.document_b64),
    bboxes: data.bboxes || [],
  };
}

/**
 * Parse multipart/form-data response and return the `document` part bytes.
 * @param {Response} response
 */
async function readMultipartDocument(response) {
  const ct = response.headers.get("content-type") || "";
  const boundaryMatch = /boundary=([^;]+)/i.exec(ct);
  if (!boundaryMatch) {
    throw new Error("Risposta redact non valida (boundary mancante).");
  }
  const boundary = boundaryMatch[1].trim().replace(/^"|"$/g, "");
  const raw = new Uint8Array(await response.arrayBuffer());
  const marker = `--${boundary}`;
  const markerBytes = new TextEncoder().encode(marker);

  const parts = [];
  let start = 0;
  for (let i = 0; i < raw.length - markerBytes.length; i += 1) {
    let match = true;
    for (let j = 0; j < markerBytes.length; j += 1) {
      if (raw[i + j] !== markerBytes[j]) { match = false; break; }
    }
    if (match) {
      if (start > 0) parts.push(raw.slice(start, i));
      start = i + markerBytes.length;
      if (raw[start] === 45 && raw[start + 1] === 45) break;
      if (raw[start] === 13 && raw[start + 1] === 10) start += 2;
      i = start - 1;
    }
  }

  for (const part of parts) {
    let headerEnd = -1;
    for (let i = 0; i < part.length - 3; i += 1) {
      if (part[i] === 13 && part[i + 1] === 10 && part[i + 2] === 13 && part[i + 3] === 10) {
        headerEnd = i;
        break;
      }
    }
    if (headerEnd < 0) continue;
    const headerText = new TextDecoder().decode(part.slice(0, headerEnd));
    if (!/name="document"/i.test(headerText)) continue;
    let body = part.slice(headerEnd + 4);
    if (body.length >= 2 && body[body.length - 2] === 13 && body[body.length - 1] === 10) {
      body = body.slice(0, -2);
    }
    return body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength);
  }

  throw new Error("Documento redatto non trovato nella risposta.");
}

/**
 * @param {string} jobId
 * @param {number} fileIndex
 * @param {object[]} bboxes
 * @param {string|null} [sessionId] sessione Stripe pagata (flusso a consumo)
 */
export async function redactPdf(jobId, fileIndex, bboxes, sessionId = null) {
  const fd = new FormData();
  fd.append("job_id", jobId);
  fd.append("file_index", String(fileIndex));
  fd.append("bboxes", JSON.stringify(bboxes));
  if (sessionId) fd.append("session_id", sessionId);

  const res = await fetch("/api/redact", { method: "POST", body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(messaggioErrore(res, err));
  }
  return readMultipartDocument(res);
}

/**
 * @param {ArrayBuffer} pdfBytes
 * @param {string} filename
 */
export async function downloadPdfFromApi(pdfBytes, filename) {
  const fd = new FormData();
  const name = filename || "document.pdf";
  fd.append("document", new Blob([pdfBytes], { type: "application/pdf" }), name);
  fd.append("filename", name);

  const res = await fetch("/api/download", { method: "POST", body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Download ${res.status}`);
  }
  return res.arrayBuffer();
}

export function savePdfToDisk(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
